/*
 * shipping-calculator.js
 * ---------------------------------------------------------------------------
 * "Calcular frete" (CEP) shipping-rate lookup — real rates via Shopify's AJAX
 * Cart API (`GET /cart/shipping_rates.json`), the same technique our old
 * Minimog theme used for its cart shipping estimator. Adapted for this store,
 * which is pt-BR/BRL-only (see CLAUDE.md): the destination country is
 * hardcoded to BR, so there's no country/province picker — just a CEP field.
 *
 * Drives any `[data-shipping-calc]` container: the product-page C6 block
 * (blocks/enfoque-c6.liquid) and the always-on cart page/drawer widget
 * (snippets/enfoque-shipping-calculator.liquid). Document-delegated so it
 * survives Theme-Editor section re-renders and cart-drawer re-fetches.
 *
 * `window.shippingCalculatorStrings` (layout/theme.liquid) supplies the
 * localized copy. Rates come back in cents, formatted with
 * theme.Currency.formatMoney (theme.js, loaded first — see script order).
 *
 * `shipping_rates.json` REQUIRES a province for a BR address — verified
 * against the live store: omitting it 422s with {"province":["Selecione um
 * estado"]}, even though the UI only asks for a CEP. So each lookup resolves
 * the state first via ViaCEP (viacep.com.br — free, keyless, CORS-enabled,
 * the standard Brazilian CEP→address API) and feeds its `uf` in as
 * shipping_address[province] before calling Shopify.
 *
 * PRODUCT PAGE (issue #35): Shopify's estimator prices the CART, and an empty
 * cart 422s — so on a product page the C6 block asks our own freight Worker
 * instead (`GET /simular`, enfoque-app-frete#13), for the selected variant ×
 * quantity. The Worker runs the checkout's very rules, so the price and date
 * match what the checkout will show. The same module (`window.enfoqueFrete`)
 * feeds the C6 card's title, "Receba até dd/mm" (decision D2, option D):
 * ONE date, production + transport, the fastest delivery's.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  // Same glyph as snippets/icon-loading.liquid, inlined because .js assets
  // can't {% render %} a Liquid snippet. `at-icon at-spin` reuses the
  // existing spin keyframe (assets/theme.css.liquid).
  var SPINNER_SVG = '<svg class="at-icon at-spin enfc-frete__spinner" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 26.349 26.35"><circle cx="13.792" cy="3.082" r="3.082"/><circle cx="13.792" cy="24.501" r="1.849"/><circle cx="6.219" cy="6.218" r="2.774"/><circle cx="21.365" cy="21.363" r="1.541"/><circle cx="3.082" cy="13.792" r="2.465"/><circle cx="24.501" cy="13.791" r="1.232"/><path d="M4.694,19.84c-0.843,0.843-0.843,2.207,0,3.05c0.842,0.843,2.208,0.843,3.05,0c0.843-0.843,0.843-2.207,0-3.05 C6.902,18.996,5.537,18.988,4.694,19.84z"/><circle cx="21.364" cy="6.218" r="0.924"/></svg>';

  function cepDigits(value) {
    return (value || '').replace(/\D/g, '');
  }

  function strings() {
    return window.shippingCalculatorStrings || {};
  }

  // ---- Own freight Worker (issue #35) -------------------------------------
  var CEP_KEY = 'enfoque:cep';
  // Praça da Sé — the date shown before the buyer gives a CEP (decision 03/11:
  // "Receba até … em São Paulo", replaced by theirs once they type one).
  var DEFAULT_CEP = '01001000';

  function simulateUrl() {
    return window.enfoqueFreteUrl || '';
  }

  function savedCep() {
    try { return cepDigits(window.localStorage.getItem(CEP_KEY)); } catch (e) { return ''; }
  }

  function saveCep(cep) {
    try { window.localStorage.setItem(CEP_KEY, cep); } catch (e) { /* private mode: just not remembered */ }
  }

  // The product's variants as rendered by Liquid: { "<variant id>": [grams, price_cents] }.
  function readProduct(el) {
    try {
      return { id: el.getAttribute('data-frete-product'), variants: JSON.parse(el.getAttribute('data-frete-variants') || '{}'), section: el.getAttribute('data-frete-section') };
    } catch (e) {
      return null;
    }
  }

  // What the buy box has selected right now: the product form's variant id and the
  // quantity field, both keyed by the section id (blocks/cta.liquid).
  function selection(product) {
    var form = document.getElementById('product-form-' + product.section);
    var idInput = form && form.querySelector('input[name="id"]');
    var qtyInput = document.getElementById('Quantity-' + product.section);
    var variantId = (idInput && idInput.value) || Object.keys(product.variants)[0];
    var qty = parseInt(qtyInput && qtyInput.value, 10);
    return { variantId: String(variantId), qty: qty > 0 ? qty : 1 };
  }

  // Worker rates (the CarrierService body) → the shape showRates() already draws:
  // { name, price (cents string, formatMoney-ready), delivery_range: [from, to],
  //   code }. Dates arrive as "2026-11-16 12:00:00 -0300"; the day is what counts.
  function normalizeRates(rates) {
    return (rates || []).map(function (r) {
      return {
        name: r.service_name,
        code: r.service_code,
        price: String(r.total_price),
        delivery_range: [String(r.min_delivery_date || '').slice(0, 10), String(r.max_delivery_date || '').slice(0, 10)],
      };
    }).sort(function (a, b) {
      // Deliveries by price, pickup last: it answers "where", not "how much to ship".
      var pa = a.code === 'retirada', pb = b.code === 'retirada';
      if (pa !== pb) return pa ? 1 : -1;
      return Number(a.price) - Number(b.price);
    });
  }

  // Promise of normalized rates for one variant × qty to a CEP. Rejects on any
  // non-200 (the Worker's 503 = "the checkout would show backup rates").
  function simulate(cep, product, sel) {
    var v = product.variants[sel.variantId];
    if (!simulateUrl() || !v) return Promise.reject(new Error('simulate-unavailable'));
    var item = [product.id, sel.variantId, sel.qty, Math.round(v[0] || 0), Math.round(v[1] || 0)].join(':');
    var url = simulateUrl() + '?cep=' + encodeURIComponent(cep) + '&i=' + encodeURIComponent(item);
    return fetchJSON(url).then(function (data) { return normalizeRates(data && data.rates); });
  }

  // The date "Receba até" promises: the FASTEST delivery's latest day (Diogo,
  // 03/11), the cheaper one on a tie — pickup is not a delivery.
  function etaRate(rates) {
    var deliveries = (rates || []).filter(function (r) { return r.code !== 'retirada'; });
    deliveries.sort(function (a, b) {
      if (a.delivery_range[1] !== b.delivery_range[1]) return a.delivery_range[1] < b.delivery_range[1] ? -1 : 1;
      return Number(a.price) - Number(b.price);
    });
    return deliveries.length ? deliveries[0] : null;
  }

  window.enfoqueFrete = { simulate: simulate, readProduct: readProduct, selection: selection, etaRate: etaRate, savedCep: savedCep, saveCep: saveCep, defaultCep: DEFAULT_CEP };

  function formatPrice(cents) {
    var format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    return (window.theme && window.theme.Currency) ? theme.Currency.formatMoney(cents, format) : cents;
  }

  function showMessage(result, text, isError) {
    result.classList.toggle('is-error', !!isError);
    result.innerHTML = '';
    var p = document.createElement('p');
    p.className = 'enfc-frete__msg';
    p.textContent = text;
    result.appendChild(p);
    result.style.display = 'block';
  }

  function showLoading(result) {
    result.classList.remove('is-error');
    result.innerHTML = '<p class="enfc-frete__msg enfc-frete__msg--loading">' + SPINNER_SVG + '<span>' + (strings().calculating || '…') + '</span></p>';
    result.style.display = 'block';
  }

  // Shopify's delivery_range is a same-format [earliest, latest] ISO date
  // pair — for a same-day pickup rate both entries are identical, which
  // read as a confusing duplicated date. Business-day count matches the
  // "5 a 8 dias úteis" copy already used in blocks/enfoque-c6-option.liquid's
  // presets, with the actual date alongside it for people who want it.
  function businessDaysBetween(from, to) {
    var d = new Date(from.getTime());
    var count = 0;
    while (d < to) {
      d.setDate(d.getDate() + 1);
      var day = d.getDay();
      if (day !== 0 && day !== 6) count++;
    }
    return count;
  }

  function formatShortDate(date) {
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  }

  function formatDeliveryRange(range) {
    if (!range || range.length < 2 || !range[0] || !range[1]) return '';
    var today = new Date();
    today.setHours(0, 0, 0, 0);
    var start = new Date(range[0] + 'T00:00:00');
    var end = new Date(range[1] + 'T00:00:00');
    if (isNaN(start) || isNaN(end)) return '';

    var sameDate = range[0] === range[1];
    var startDays = businessDaysBetween(today, start);

    if (sameDate && startDays <= 0) return 'Hoje';

    if (sameDate) {
      return (startDays === 1 ? '1 dia útil' : startDays + ' dias úteis') + ' (' + formatShortDate(start) + ')';
    }

    var endDays = businessDaysBetween(today, end);
    return startDays + ' a ' + endDays + ' dias úteis (' + formatShortDate(start) + ' a ' + formatShortDate(end) + ')';
  }

  // Product-page card (option D, issue #35): the fastest option first, tagged, then
  // the rest by price, each dated "até qua., 11/11" — the same words as the title.
  function etaOrder(rates) {
    var fastest = etaRate(rates);
    if (!fastest) return { list: rates, fastest: null };
    return { list: [fastest].concat(rates.filter(function (r) { return r !== fastest; })), fastest: fastest };
  }

  function showRates(result, rates, root) {
    result.classList.remove('is-error');
    result.innerHTML = '';
    var eta = root && root.getAttribute('data-shipping-calc-style') === 'eta';
    var ordered = eta ? etaOrder(rates) : { list: rates, fastest: null };
    var after = root && root.querySelector('[data-shipping-calc-after]');
    if (after) after.hidden = false;
    ordered.list.forEach(function (rate) {
      var row = document.createElement('div');
      row.className = 'enfc-frete__opt';

      var info = document.createElement('div');
      var name = document.createElement('div');
      name.className = 'n';
      name.textContent = rate.name || '';
      if (eta && rate === ordered.fastest && root.getAttribute('data-eta-fastest')) {
        var tag = document.createElement('span');
        tag.className = 'enfc-frete__tag';
        tag.textContent = root.getAttribute('data-eta-fastest');
        name.appendChild(tag);
      }
      info.appendChild(name);
      var rangeText = eta ? 'até ' + formatEtaDate(rate.delivery_range[1]) : formatDeliveryRange(rate.delivery_range);
      if (rangeText) {
        var range = document.createElement('div');
        range.className = 'd';
        range.textContent = rangeText;
        info.appendChild(range);
      }

      var price = document.createElement('span');
      price.className = 'p';
      price.innerHTML = formatPrice(rate.price);

      row.appendChild(info);
      row.appendChild(price);
      result.appendChild(row);
    });
    result.style.display = 'block';
  }

  // Every lookup gives up after 10 s: a slow third party (ViaCEP, the carrier) must
  // never leave "Calculando…" on screen forever (seen on 03/11).
  var FETCH_TIMEOUT_MS = 10000;

  function fetchJSON(url) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, FETCH_TIMEOUT_MS) : null;
    var opts = { headers: { Accept: 'application/json' } };
    if (controller) opts.signal = controller.signal;
    return fetch(url, opts).finally(function () { if (timer) clearTimeout(timer); }).then(function (res) {
      return res.json().catch(function () { return null; }).then(function (data) {
        if (!res.ok) {
          // A rates lookup on a product page with an EMPTY cart 422s — that's not a
          // customer-facing error, it's "the lookup can't run yet". The caller flags it
          // (`ratesError`) and degrades to the labelled estimate instead of a red error.
          var err = new Error('http-' + res.status);
          err.httpError = true;
          err.data = data;
          throw err;
        }
        return data;
      });
    });
  }

  // The fallback options (blocks/enfoque-c6.liquid children) are merchant-entered
  // ESTIMATES, not quotes: only ever shown after a lookup ran and produced nothing
  // usable, and only when the merchant actually filled some in — an empty fallback
  // container (label + caption but no `.enfc-frete__opt` row) must never be shown.
  function hasFallbackOptions(fallback) {
    return !!(fallback && fallback.querySelector('.enfc-frete__opt'));
  }

  function showEstimateOrMessage(result, fallback) {
    if (hasFallbackOptions(fallback)) {
      result.style.display = 'none';
      fallback.style.display = 'block';
      return;
    }
    var noCart = fallback && fallback.getAttribute('data-shipping-calc-nocart');
    showMessage(result, noCart || strings().noRate || 'Sem opções de frete para este CEP.', false);
  }

  // Analytics signal (assets/lead-events.js → GA4 `shipping_quote`, not a lead).
  // UF only — never the CEP itself, which is personal data.
  function trackQuote(uf, result, rates) {
    if (typeof window.enfoqueTrack !== 'function') return;
    var data = { uf: uf || '', result: result, rates_count: rates ? rates.length : 0 };
    if (rates && rates.length) {
      data.min_price = Math.min.apply(null, rates.map(function (r) { return parseFloat(r.price) || 0; }));
    }
    window.enfoqueTrack('shipping_quote', data);
  }

  function calculate(root) {
    var input = root.querySelector('[data-shipping-calc-input]');
    var btn = root.querySelector('[data-shipping-calc-btn]');
    var result = root.querySelector('[data-shipping-calc-result]');
    var fallback = root.querySelector('[data-shipping-calc-fallback]');
    if (!input || !result) return;

    var cep = cepDigits(input.value);
    if (fallback) fallback.style.display = 'none';
    // The product-page block's idle hint ("Informe seu CEP…") has done its job once a
    // lookup starts — the result area speaks from here on.
    var hint = root.querySelector('[data-shipping-calc-hint]');
    if (hint) hint.style.display = 'none';

    if (cep.length !== 8) {
      input.focus();
      showMessage(result, strings().error || 'CEP inválido.', true);
      return;
    }

    if (btn) btn.disabled = true;
    showLoading(result);
    var uf = '';

    // Product page: the Worker needs no state, so ask it directly — no ViaCEP round
    // trip in front of the answer (and no wait on it when it is slow).
    var product = root.hasAttribute('data-frete-product') ? readProduct(root) : null;
    if (product && simulateUrl()) {
      // ViaCEP still runs, in parallel, only for the analytics UF.
      var ufLookup = fetchJSON('https://viacep.com.br/ws/' + cep + '/json/').then(function (a) { return (a && a.uf) || ''; }, function () { return ''; });
      var track = function (outcome, list) { ufLookup.then(function (u) { trackQuote(u, outcome, list); }); };
      simulate(cep, product, selection(product)).then(function (rates) {
        if (btn) btn.disabled = false;
        saveCep(cep);
        document.dispatchEvent(new CustomEvent('enfoque:frete-quote', { detail: { cep: cep, rates: rates, product: product.id } }));
        // Pickup is the native `pickup` block's job on the product page (#66).
        var deliveries = rates.filter(function (r) { return r.code !== 'retirada'; });
        if (deliveries.length) {
          showRates(result, deliveries, root);
          track('rates', deliveries);
        } else {
          showEstimateOrMessage(result, fallback);
          track('no_rates');
        }
      }, function () {
        if (btn) btn.disabled = false;
        showEstimateOrMessage(result, fallback);
        track('estimate');
      });
      return;
    }

    fetchJSON('https://viacep.com.br/ws/' + cep + '/json/')
      .then(function (address) {
        if (!address || address.erro) {
          var err = new Error('cep-not-found');
          err.notFound = true;
          throw err;
        }
        uf = address.uf || '';
        var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
        var url = cartUrl + '/shipping_rates.json?shipping_address%5Bzip%5D=' +
          encodeURIComponent(cep) + '&shipping_address%5Bcountry%5D=BR&shipping_address%5Bprovince%5D=' +
          encodeURIComponent(address.uf || '');
        return fetchJSON(url).catch(function (err) {
          err.ratesError = true;
          throw err;
        });
      })
      .then(function (data) {
        if (btn) btn.disabled = false;
        if (data && Array.isArray(data.shipping_rates)) {
          if (data.shipping_rates.length > 0) {
            showRates(result, data.shipping_rates, root);
            trackQuote(uf, 'rates', data.shipping_rates);
          } else {
            showEstimateOrMessage(result, fallback);
            trackQuote(uf, 'no_rates');
          }
        } else {
          showMessage(result, strings().error || 'Não foi possível calcular o frete.', true);
        }
      })
      .catch(function (err) {
        if (btn) btn.disabled = false;
        if (err && err.ratesError) {
          // The CEP resolved fine; Shopify just couldn't price it (typically an empty
          // cart on a product page). Show the labelled estimate, or say so plainly.
          showEstimateOrMessage(result, fallback);
          trackQuote(uf, 'estimate');
          return;
        }
        var message = (err && err.notFound) ? (strings().cepNotFound || 'CEP não encontrado.') : (strings().error || 'Não foi possível calcular o frete.');
        showMessage(result, message, true);
      });
  }

  // ---- "Receba até dd/mm" — the C6 card's title (D2, issue #35 option D) ----
  function formatEtaDate(iso) {
    var d = new Date(iso + 'T12:00:00');
    if (isNaN(d)) return '';
    return d.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' });
  }

  function formatCep(cep) {
    return cep.slice(0, 5) + '-' + cep.slice(5);
  }

  function setEtaVisible(root, visible) {
    var el = root.querySelector('[data-delivery-eta]');
    var fallback = root.querySelector('[data-eta-fallback]');
    if (el) el.hidden = !visible;
    if (fallback) fallback.hidden = visible;
  }

  function renderEta(root, rates, cep) {
    var el = root.querySelector('[data-delivery-eta]');
    var rate = etaRate(rates);
    if (!el || !rate) {
      setEtaVisible(root, false);
      return;
    }
    el.querySelector('[data-eta-date]').textContent = formatEtaDate(rate.delivery_range[1]);
    var custom = cep !== DEFAULT_CEP;
    var where = el.querySelector('[data-eta-where]');
    if (where) {
      where.textContent = custom
        ? (root.getAttribute('data-eta-cep-where') || '').replace('[cep]', formatCep(cep))
        : (root.getAttribute('data-eta-default-where') || '');
    }
    // Pre-CEP the form sits right under the title, so a "change" link would only
    // repeat it; once a CEP is known the link takes the buyer back to the field.
    var change = el.querySelector('[data-eta-change]');
    if (change) change.hidden = !custom;
    el.classList.remove('is-loading');
    setEtaVisible(root, true);
  }

  // One Worker call per card: the title's date, and — when the buyer already gave a
  // CEP on this or an earlier page — the options too, so the card opens answered.
  function refreshEta(root, variantId) {
    var product = readProduct(root);
    if (!product) return;
    var sel = selection(product);
    if (variantId) sel.variantId = String(variantId);
    var cep = savedCep() || DEFAULT_CEP;
    var el = root.querySelector('[data-delivery-eta]');
    if (el) el.classList.add('is-loading');
    simulate(cep, product, sel).then(function (rates) {
      renderEta(root, rates, cep);
      if (cep === DEFAULT_CEP) return;
      var input = root.querySelector('[data-shipping-calc-input]');
      var result = root.querySelector('[data-shipping-calc-result]');
      var hint = root.querySelector('[data-shipping-calc-hint]');
      var deliveries = rates.filter(function (r) { return r.code !== 'retirada'; });
      if (input && !input.value) input.value = formatCep(cep);
      if (result && deliveries.length) {
        if (hint) hint.style.display = 'none';
        showRates(result, deliveries, root);
      }
    }, function () {
      // No date is better than a wrong one: the plain title takes over.
      setEtaVisible(root, false);
    });
  }

  function etaRoots(scope) {
    return (scope || document).querySelectorAll('[data-shipping-calc][data-frete-product]');
  }

  // A CEP typed in the card answers the title too — no second call.
  document.addEventListener('enfoque:frete-quote', function (e) {
    etaRoots().forEach(function (root) {
      if (root.getAttribute('data-frete-product') === String(e.detail.product)) renderEta(root, e.detail.rates, e.detail.cep);
    });
  });

  // "alterar" in the title hands over to the card's own CEP field.
  document.addEventListener('click', function (e) {
    var link = e.target.closest('[data-eta-change]');
    if (!link) return;
    var root = link.closest('[data-shipping-calc]');
    var input = root && root.querySelector('[data-shipping-calc-input]');
    if (!input) return;
    e.preventDefault();
    input.focus();
    input.select();
  });

  // A new variant can change the date and every price: ask again (the Worker's cache
  // makes a repeat cheap). With a saved CEP this redraws the options as well.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
      var variant = event && event.data && event.data.variant;
      if (!variant) return;
      etaRoots().forEach(function (root) { refreshEta(root, variant.id); });
    });
  }
  document.addEventListener('shopify:section:load', function (e) {
    etaRoots(e.target).forEach(function (root) { refreshEta(root); });
  });
  etaRoots().forEach(function (root) { refreshEta(root); });

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-shipping-calc-btn]');
    if (!btn) return;
    e.preventDefault();
    var root = btn.closest('[data-shipping-calc]');
    if (root) calculate(root);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var input = e.target.closest('[data-shipping-calc-input]');
    if (!input) return;
    e.preventDefault();
    var root = input.closest('[data-shipping-calc]');
    if (root) calculate(root);
  });
})();
